import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { RALLYANCE_RECLAMATOR_ADMIN } from '../Interfaces/reclamations';
import { AdminTypes, LoginUser, ReportsManagementCanSeeDictionary, ReportsManagementCanSeeKeys, Users } from '@interfaces/users';
import { FormattedLog } from '../Utility/FormattedConsole';
import { LoadingService } from './loading.service';
import { LS_KEYS, StorageService } from './storage.service';
import { StoreService } from './store.service';

export const KNOWN_ADMIN_TYPES = ['management', 'vansale'] as const;
@Injectable({
	providedIn: 'root',
})
export class UserService {
	private _loading = inject(LoadingService);
	agentEmail$ = new BehaviorSubject('');
	isTestUser$ = new BehaviorSubject(false);
	isAdmin$ = new BehaviorSubject(false);
	isRallyanceReclamator$ = new BehaviorSubject(false);
	isVanSaleUser$ = new BehaviorSubject(false);
	isManagementUser$ = new BehaviorSubject(false);

	isCheckingUserLoggedStatus$ = new BehaviorSubject(false);
	lastTimeUserLoggedChecked: number = 0;

	private reportsDictionary$ = new BehaviorSubject<Record<ReportsManagementCanSeeKeys, { text: string; path: string }>>({
		dashboardTwo: { text: 'ניהול הזמנות', path: '/mobilitydashbord2' },
		receiptManagement: { text: 'ניהול קבלות', path: '/dashboard-receipts' },
		adminBizoaHevra: { text: 'ביצועים חברה', path: '/admin-bizoa-hevra' },
		dohotLekohotHovotAdmin: { text: 'ריכוז חובות', path: '/dohot-lekohot-hovot-admin' },
	});

	reportsDictionaryDisplay$: Observable<{ text: string; path: string }[]> = this.reportsDictionary$.pipe(
		map(dict =>
			Object.keys(dict)
				.filter(key => this.reportsDictionary[key])
				.map(key => dict[key]),
		),
	);

	reportsDictionary: Record<ReportsManagementCanSeeKeys, boolean> = {
		dashboardTwo: false,
		receiptManagement: false,
		adminBizoaHevra: false,
		dohotLekohotHovotAdmin: false,
	};

	constructor(private _ss: StoreService, private _storage: StorageService) {
		this.handleInit();
	}

	updateEmail(email: string) {
		this.agentEmail$.next(email);
	}

	handleInit() {
		const emailFromStorage = localStorage.getItem('AgentEmail');
		if (emailFromStorage) {
			this.updateEmail(emailFromStorage);
		}

		const admin: AdminTypes | null = this._ss.Admin || (localStorage.getItem('Admin') as AdminTypes | null);
		if (admin) {
			this.isAdmin$.next(admin === 'true' || admin.includes('true'));
			this.isRallyanceReclamator$.next(admin.toLowerCase().includes(RALLYANCE_RECLAMATOR_ADMIN));
			this.isVanSaleUser$.next(admin.toLowerCase().includes('vansale'));
			this.isManagementUser$.next(admin.toLowerCase().includes('management'));
		}

		this.handleManagementReportsCanSee();
	}

	handleManagementReportsCanSee() {
		if (!this.isManagementUser$.value) {
			this.changeAllManagementReportsCanSee(false);
			return;
		}

		if (!this._ss.CustomerInformation?.Management_ReportsCanSee) return;
		const { Management_ReportsCanSee } = this._ss.CustomerInformation;

		if (Management_ReportsCanSee === '0') {
			this.changeAllManagementReportsCanSee(true);
			return;
		}

		const reports: string[] = Management_ReportsCanSee.split('_');

		const { dashboardTwo, receiptManagement } = ReportsManagementCanSeeDictionary;

		this.reportsDictionary.dashboardTwo = reports.includes(dashboardTwo);
		this.reportsDictionary.receiptManagement = reports.includes(receiptManagement);
	}

	changeAllManagementReportsCanSee(newValue: boolean) {
		for (const report in this.reportsDictionary) {
			if (Object.prototype.hasOwnProperty.call(this.reportsDictionary, report)) {
				this.reportsDictionary[report] = newValue;
			}
		}
	}

	updateTestUserState(newValue: boolean) {
		this.isTestUser$.next(newValue);
	}

	updateUserParamsAndInfo(data: { verifiedUser: LoginUser }) {
		const { verifiedUser } = data;
		this._ss.UserNameConnected = verifiedUser.UserName;
		this._ss.AgentIdConnected = verifiedUser.AgentID;
	}

	refreshUserParams() {
		this.handleInit();
	}

	isLoggedIn() {
		return this._storage.doesKeyExistInStorage('UserName') && this._storage.doesKeyExistInStorage('AgentId');
	}

	shouldDisconnetUser(user: Users | { Logged: string }) {
		const { Logged } = user;
		return Logged === '2';
	}

	initiateUserLoggedStatusCheck() {
		if (this.isCheckingUserLoggedStatus$.value || !this._ss.AgentIdConnected || this.lastTimeUserLoggedChecked + 10000 > Date.now()) {
			return;
		}

		this.isCheckingUserLoggedStatus$.next(true);

		const agentId = this._ss.AgentIdConnected;

		this._ss.checkCurrentLoggedStatusForUser(agentId).subscribe({
			next: res => {
				this.isCheckingUserLoggedStatus$.next(false);
				if (typeof res === 'string' || typeof res.body === 'string' || !res.body.recordset.length) {
					return;
				}

				this.handleShouldDisconnectUser(res.body.recordset[0]);
			},
			error: err => {
				this._ss.GetAllAgents().subscribe({
					next: res => {
						this.isCheckingUserLoggedStatus$.next(false);
						if (typeof res === 'string') return;
						this.handleShouldDisconnectUser(res.recordset);
					},
					error: err => {
						this.isCheckingUserLoggedStatus$.next(false);
						console.log(err);
					},
				});
				return;
			},
		});
	}

	handleShouldDisconnectUser(input: Users | Users[] | { Logged: string }) {
		const user = Array.isArray(input) ? input.find(u => u.AgentID === this._ss.AgentIdConnected) : input;

		this.lastTimeUserLoggedChecked = Date.now();
		if (!this.shouldDisconnetUser(user)) return;
		this.isCheckingUserLoggedStatus$.next(true);

		// this.LoggoutPop = true;

		this._loading.changeLoadingStatuses({ isLoading: true, newMessage: 'הנך מנותק מהמערכת' });
		setTimeout(() => {
			this._storage.clearAllSavedData();
			location.reload();
		}, 500);
	}
}
